// @flow
import { useEffect, useMemo, useState, Fragment } from 'react';
import { Global, css } from '@emotion/core';
import axios from 'axios';
import capitalize from 'lodash/capitalize';
import LanguageDetector from 'i18next-browser-languagedetector';
import { Redirect } from '@reach/router';
import type { PageContextType } from '../components/PageContext';
import {
  GEO_LOCATION_STORAGE_KEY,
  USER_LANGUAGE_STORAGE_KEY,
  ROOT_LOCALE
} from './constants';

let browserLanguageValue;
if (typeof window !== 'undefined') {
  const languageDetector = new LanguageDetector(
    {
      // Extracted from i18next/src/LanguageUtils.js
      languageUtils: {
        formatLanguageCode(code) {
          // http://www.iana.org/assignments/language-tags/language-tags.xhtml
          if (typeof code === 'string' && code.indexOf('-') > -1) {
            const specialCases = [
              'hans',
              'hant',
              'latn',
              'cyrl',
              'cans',
              'mong',
              'arab'
            ];
            const p = code.split('-');

            if (p.length === 2) {
              p[0] = p[0].toLowerCase();
              p[1] = p[1].toUpperCase();

              if (specialCases.indexOf(p[1].toLowerCase()) > -1) {
                p[1] = capitalize(p[1].toLowerCase());
              }
            } else if (p.length === 3) {
              p[0] = p[0].toLowerCase();

              // if length 2 guess it's a country
              if (p[1].length === 2) p[1] = p[1].toUpperCase();
              if (p[0] !== 'sgn' && p[2].length === 2) {
                p[2] = p[2].toUpperCase();
              }

              if (specialCases.indexOf(p[1].toLowerCase()) > -1) {
                p[1] = capitalize(p[1].toLowerCase());
              }
              if (specialCases.indexOf(p[2].toLowerCase()) > -1) {
                p[2] = capitalize(p[2].toLowerCase());
              }
            }

            return p.join('-');
          }

          return code;
        },
        isWhitelisted: () => true
      }
    },
    {
      order: ['navigator', 'htmlTag']
    }
  );
  browserLanguageValue = languageDetector.detect() || ROOT_LOCALE;
  // We are only interested in the general language, country specific comes from geoip
  [browserLanguageValue] = browserLanguageValue.toLowerCase().split('-');
}

export function getGeolocation(): Promise<?string> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  const storedLocation = localStorage.getItem(GEO_LOCATION_STORAGE_KEY);
  if (storedLocation) return Promise.resolve(storedLocation);
  return axios(`${CONFIG.dinerApi}/v2/geolocation`)
    .then(({ data: { country } }) => {
      localStorage.setItem(GEO_LOCATION_STORAGE_KEY, country);
      return country;
    })
    .catch((error) => {
      console.error(error);
      return '';
    });
}

// https://stackoverflow.com/a/326076/1413689
function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

const useCountryDetection = (pageContext: PageContextType) => {
  const {
    links: { alternates, hasCountrySpecificAlternates },
    story: { full_slug: fullSlug }
  } = pageContext;
  if (
    inIframe() ||
    (typeof navigator !== 'undefined' &&
      /bot|googlebot|crawler|spider|robot|crawling|lighthouse/i.test(
        navigator.userAgent
      ))
  ) {
    // for bots and the storyblok editor iFrame we should not do any redirecting
    return null;
  }

  const staticData = useMemo(() => {
    let localStorageLanguage;
    let localStorageCountry;
    if (typeof window !== 'undefined') {
      localStorageLanguage = localStorage.getItem(USER_LANGUAGE_STORAGE_KEY);
      localStorageCountry = localStorage.getItem(GEO_LOCATION_STORAGE_KEY);
    }
    const [pathLocale] = fullSlug.split('/');
    const languageValue = localStorageLanguage || browserLanguageValue;
    const countryValue = localStorageCountry || '';
    // if the user's locale has been detected before and it matches the current page
    if (
      pathLocale === languageValue ||
      pathLocale === `${languageValue}-${countryValue}`
    ) {
      localStorage.setItem(USER_LANGUAGE_STORAGE_KEY, languageValue);
      localStorage.setItem(GEO_LOCATION_STORAGE_KEY, countryValue);
      return null;
    }

    const result = {};
    for (let i = 0; i < alternates.length; i += 1) {
      const alternateLink = alternates[i];
      if (alternateLink.lang === 'x-default') {
        result.defaultRedirect = alternateLink;
      } else {
        const exactLanguageMatch =
          alternateLink.lang.toLowerCase() === languageValue;
        if (
          (!hasCountrySpecificAlternates && exactLanguageMatch) ||
          alternateLink.lang.toLowerCase() ===
            `${languageValue}-${countryValue}`
        ) {
          result.exactMatch = alternateLink;
        } else if (exactLanguageMatch) {
          result.languageOnlyMatch = alternateLink;
        } else if (
          alternateLink.lang.split('-')[0].toLowerCase() === languageValue
        ) {
          result.partialMatch = alternateLink;
        }
      }
    }

    return result;
  });
  if (!staticData) return null;

  const {
    exactMatch,
    defaultRedirect,
    partialMatch,
    languageOnlyMatch
  } = staticData;
  let defaultRedirectNode = null;
  if (exactMatch) {
    defaultRedirectNode = <Redirect to={exactMatch.slug} replace noThrow />;
  } else if (!languageOnlyMatch && !partialMatch) {
    defaultRedirectNode = (
      <Redirect to={defaultRedirect.slug} replace noThrow />
    );
  } else if (languageOnlyMatch && !partialMatch) {
    defaultRedirectNode = (
      <Redirect to={languageOnlyMatch.slug} replace noThrow />
    );
  } else if (!hasCountrySpecificAlternates) {
    defaultRedirectNode = (
      <Redirect
        to={(languageOnlyMatch || partialMatch || defaultRedirect).slug}
        replace
        noThrow
      />
    );
  }
  const [redirectNode, setRedirectNode] = useState(defaultRedirectNode);

  const renderNode = (
    <Fragment>
      {redirectNode}
      <Global
        styles={css`
          body:before {
            opacity: 1;
            pointer-events: auto;
            transition: none;
          }
        `}
      />
    </Fragment>
  );

  if (redirectNode) return renderNode;
  useEffect(() => {
    // if partialMatch, should check for country
    getGeolocation()
      .then((country) => {
        if (country) {
          for (let i = 0; i < alternates.length; i += 1) {
            const alternateLink = alternates[i];
            if (
              alternateLink.lang.toLowerCase() ===
              `${browserLanguageValue}-${country}`.toLowerCase()
            ) {
              setRedirectNode(
                <Redirect to={alternateLink.slug} replace noThrow />
              );
              return;
            }
          }
        }

        let redirectTo = defaultRedirect.slug;
        if (languageOnlyMatch) {
          redirectTo = languageOnlyMatch.slug;
        }

        setRedirectNode(<Redirect to={redirectTo} replace noThrow />);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);
  return renderNode;
};

export default useCountryDetection;
